import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  School as SchoolIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useSEO } from '../hooks/useSEO';

// 导入图片
import publicSchool1 from '../assets/images/power_of_attorney/public/image_11.jpg';
import publicSchool2 from '../assets/images/power_of_attorney/public/image_12.jpg';
import publicSchool3 from '../assets/images/power_of_attorney/public/image_13.jpg';
import publicSchool4 from '../assets/images/power_of_attorney/public/image_14.jpg';
import publicSchool5 from '../assets/images/power_of_attorney/public/image_15.jpg';
import publicSchool6 from '../assets/images/power_of_attorney/public/image_16.jpg';
import privateSchool1 from '../assets/images/power_of_attorney/private/image_17.jpg';
import privateSchool2 from '../assets/images/power_of_attorney/private/image_18.jpg';
import privateSchool3 from '../assets/images/power_of_attorney/private/image_19.jpg';
import privateSchool4 from '../assets/images/power_of_attorney/private/image_20.jpg';
import privateSchool5 from '../assets/images/power_of_attorney/private/image_21.jpg';
import privateSchool6 from '../assets/images/power_of_attorney/private/image_22.jpg';
import privateSchool7 from '../assets/images/power_of_attorney/private/image_23.jpg';

// 带加载效果的图片组件
const ImageWithLoading = ({ src, alt, style, sx, ...props }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: loading ? '200px' : 'auto',
        ...sx
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.05)',
            zIndex: 1
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      {error ? (
        <Box
          sx={{
            width: '100%',
            minHeight: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.05)',
            color: 'text.secondary'
          }}
        >
          <Typography variant="body2">图片加载失败</Typography>
        </Box>
      ) : (
        <img
          src={src}
          alt={alt}
          style={{
            ...style,
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.3s ease',
            width: '100%',
            visibility: loading ? 'hidden' : 'visible'
          }}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />
      )}
    </Box>
  );
};

// 图片画廊组件（网格视图 + 轮播视图）
const ImageGallery = ({ items, title }) => {
  const theme = useTheme();
  const [isCarouselMode, setIsCarouselMode] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const getPrevIndex = (index) => (index - 1 + items.length) % items.length;
  const getNextIndex = (index) => (index + 1) % items.length;
  
  const prevIndex = getPrevIndex(currentIndex);
  const nextIndex = getNextIndex(currentIndex);

  const handleImageClick = (index) => {
    setCurrentIndex(index);
    setIsCarouselMode(true);
  };

  const handleCloseCarousel = () => {
    setIsCarouselMode(false);
  };

  // 网格视图
  if (!isCarouselMode) {
    return (
      <Grid container spacing={3}>
        {items.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Box
              onClick={() => handleImageClick(index)}
              sx={{
                cursor: 'pointer',
                position: 'relative',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: theme.shadows[2],
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: theme.shadows[6]
                }
              }}
            >
              <ImageWithLoading
                src={item.image}
                alt={item.name}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  p: 2,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  点击查看详情
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  // 轮播视图
  return (
    <Box>
      {/* 关闭按钮 */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton
          onClick={handleCloseCarousel}
          sx={{
            bgcolor: 'rgba(0,0,0,0.5)',
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.7)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
        {/* 左侧缩略图 */}
        <Box
          onClick={() => setCurrentIndex(prevIndex)}
          sx={{
            cursor: 'pointer',
            opacity: 0.6,
            transition: 'all 0.3s ease',
            position: 'relative',
            flex: '1 1 auto',
            maxWidth: { xs: 'none', md: '20%' },
            display: { xs: 'none', md: 'block' },
            '&:hover': {
              opacity: 1,
              transform: 'scale(1.05)'
            }
          }}
        >
          <ImageWithLoading
            src={items[prevIndex].image}
            alt={items[prevIndex].name}
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: theme.shadows[2]
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              p: 1,
              borderRadius: '4px',
              textAlign: 'center'
            }}
          >
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {items[prevIndex].name}
            </Typography>
          </Box>
        </Box>

        {/* 中间主图 - 占60%宽度 */}
        <Box sx={{ 
          position: 'relative', 
          width: { xs: '100%', md: '60%' },
          flexShrink: 0
        }}>
          <IconButton
            onClick={() => setCurrentIndex(prevIndex)}
            sx={{ 
              position: 'absolute', 
              left: 16, 
              top: '50%', 
              transform: 'translateY(-50%)', 
              zIndex: 3, 
              bgcolor: 'rgba(255,255,255,0.9)',
              boxShadow: theme.shadows[4],
              '&:hover': {
                bgcolor: 'rgba(255,255,255,1)'
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{ width: '100%', position: 'relative', maxHeight: '600px', overflow: 'hidden', borderRadius: '8px' }}>
            <ImageWithLoading
              src={items[currentIndex].image}
              alt={items[currentIndex].name}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '600px',
                objectFit: 'contain',
                display: 'block'
              }}
            />
            <Box sx={{ 
              position: 'absolute', 
              bottom: 0, 
              left: 0, 
              right: 0, 
              bgcolor: 'rgba(0,0,0,0.7)', 
              color: 'white', 
              p: 2,
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {items[currentIndex].name}
              </Typography>
              <Typography variant="body2">
                【点击查看】{items[currentIndex].name}招生简章
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setCurrentIndex(nextIndex)}
            sx={{ 
              position: 'absolute', 
              right: 16, 
              top: '50%', 
              transform: 'translateY(-50%)', 
              zIndex: 3, 
              bgcolor: 'rgba(255,255,255,0.9)',
              boxShadow: theme.shadows[4],
              '&:hover': {
                bgcolor: 'rgba(255,255,255,1)'
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {/* 右侧缩略图 */}
        <Box
          onClick={() => setCurrentIndex(nextIndex)}
          sx={{
            cursor: 'pointer',
            opacity: 0.6,
            transition: 'all 0.3s ease',
            position: 'relative',
            flex: '1 1 auto',
            maxWidth: { xs: 'none', md: '20%' },
            display: { xs: 'none', md: 'block' },
            '&:hover': {
              opacity: 1,
              transform: 'scale(1.05)'
            }
          }}
        >
          <ImageWithLoading
            src={items[nextIndex].image}
            alt={items[nextIndex].name}
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: theme.shadows[2]
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              p: 1,
              borderRadius: '4px',
              textAlign: 'center'
            }}
          >
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {items[nextIndex].name}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
        {currentIndex + 1} / {items.length}
      </Typography>
    </Box>
  );
};

const Partners = () => {
  const theme = useTheme();
  
  // 设置页面 SEO
  useSEO('partners');


  // 公立学校数据
  const publicSchools = [
    { name: '马来西亚国立大学', image: publicSchool1 },
    { name: '马来西亚理科大学', image: publicSchool2 },
    { name: '马来西亚博特拉大学', image: publicSchool3 },
    { name: '马来西亚理工大学', image: publicSchool4 },
    { name: '苏丹依德里斯教育大学', image: publicSchool5 },
    { name: '玛拉工艺大学', image: publicSchool6 }
  ];

  // 私立学校数据
  const privateSchools = [
    { name: '马来西亚思特雅大学', image: privateSchool1 },
    { name: '马来西亚英迪大学', image: privateSchool2 },
    { name: '马来西亚双威大学', image: privateSchool3 },
    { name: '亚太科技大学', image: privateSchool4 },
    { name: '拉曼大学', image: privateSchool5 },
    { name: '汝来大学', image: privateSchool6 },
    { name: '玛莎大学', image: privateSchool7 }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* 英雄区域 */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                mb: 3, 
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              合作院校
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
              与马来西亚排名前30的公立、私立大学长期保持良好合作关系，源源不断为马来西亚高等教育输入国内数千名优质生源，每年服务上千名学生
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* 公立学校授权 */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 4, color: '#052391', textAlign: 'center' }}>
            公立学校授权
          </Typography>
          <ImageGallery items={publicSchools} title="公立学校授权" />
        </Box>

        {/* 私立学校授权 */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 4, color: '#052391', textAlign: 'center' }}>
            私立学校授权
          </Typography>
          <ImageGallery items={privateSchools} title="私立学校授权" />
        </Box>

        {/* 合作优势 */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 6, fontWeight: 600 }}>
            合作优势
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', textAlign: 'center' }}>
                <CardContent>
                  <Box sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'primary.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SchoolIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#052391' }}>
                    官方授权
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    与马来西亚各大院校建立官方合作关系，获得正式授权，确保申请流程的权威性和可靠性。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', textAlign: 'center' }}>
                <CardContent>
                  <Box sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'secondary.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SchoolIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#052391' }}>
                    优先录取
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    凭借良好的合作关系，为我们的学生争取优先录取机会，提高申请成功率。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', textAlign: 'center' }}>
                <CardContent>
                  <Box sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'success.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SchoolIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#052391' }}>
                    专业服务
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    深入了解各院校的招生政策和要求，为学生提供最专业的申请指导和全程服务。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* 联系我们 */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            想了解更多合作院校信息？
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4 }}>
            我们的专业顾问将为您详细介绍各院校的招生政策和申请要求
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Partners;
